import { useEffect, useRef, useState, useCallback } from "react";
import { Receipt, CheckCircle, XCircle, UploadSimple, Image as ImageIcon, Warning, Eye, X } from "@phosphor-icons/react";
import { receiptApi, debtorApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import Spinner from "@/components/Spinner";
import { fmtCurrency } from "@/lib/format";

const fmt = (n) => (n == null ? "—" : fmtCurrency(n));

// Helper legible (en vez de ternarios anidados) para los estilos/descripciones de cada etiqueta OCR.
function getEtiquetaStyle(etiqueta) {
  let className = "bg-destructive/10 text-destructive";
  let description = "No se pudo determinar coincidencia con la deuda asociada.";
  let isSuccess = false;

  if (etiqueta === "VALIDACIÓN_PENDIENTE") {
    className = "bg-success/10 text-success";
    description = "Monto coincide con la deuda. Marcada para verificación humana final.";
    isSuccess = true;
  } else if (etiqueta === "ABONO_PARCIAL") {
    className = "bg-warning/10 text-warning";
    description = "El monto del comprobante es MENOR al adeudado. Registrar como abono parcial.";
  } else if (etiqueta === "MONTO_MAYOR") {
    description = "El monto del comprobante es MAYOR al adeudado. Revisar con el cliente.";
  }

  return { className, description, isSuccess };
}

export default function ReceiptsPage() {
  const [receipts, setReceipts] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [selDebtor, setSelDebtor] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [imageError, setImageError] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([receiptApi.list(), debtorApi.list()]);
      setReceipts(r); setDebtors(d);
    } catch {
      toast.error("No se pudieron cargar los comprobantes");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) {
      toast.error("Solo imágenes JPG, PNG o WEBP");
      return;
    }
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setLoading(true);
    try {
      const r = await receiptApi.validate(f, selDebtor || null);
      setResult(r);
      toast.success("Comprobante procesado por el Agente de Visión");
      toast.warning("Recuerda verificar en tu banco que el pago esté realmente acreditado.", { duration: 8000 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error procesando imagen");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <div className="bg-card border border-border rounded-md p-6">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-md bg-primary/10 grid place-items-center text-primary">
            <Receipt size={24} weight="duotone" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-xl">Validación de comprobantes</h3>
            <p className="text-sm text-muted-foreground">
              Suba el comprobante de transferencia enviado por el deudor. El agente de visión GPT-4o extrae automáticamente monto, fecha, número de comprobante y banco de origen.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md p-6 space-y-4">
          <div>
            <Label>Asociar a deudor (opcional)</Label>
            <Select value={selDebtor} onValueChange={setSelDebtor}>
              <SelectTrigger data-testid="select-debtor-receipt"><SelectValue placeholder="Seleccione deudor…" /></SelectTrigger>
              <SelectContent>
                {debtors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.cliente_nombre} — {fmt(d.monto_deuda)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={loading}
            data-testid="upload-receipt-btn"
            className="w-full border-2 border-dashed border-border rounded-md py-10 text-center hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-60"
          >
            {loading ? (
              <div className="flex flex-col items-center gap-2">
                <Spinner size="l" />
                <div className="text-sm text-muted-foreground">Procesando con GPT-4o Vision…</div>
              </div>
            ) : (
              <>
                <UploadSimple size={32} weight="duotone" className="mx-auto text-primary mb-2" />
                <div className="font-medium">Subir comprobante</div>
                <div className="text-xs text-muted-foreground mt-1">JPG · PNG · WEBP</div>
              </>
            )}
          </button>

          {preview && (
            <div className="border border-border rounded-md overflow-hidden">
              <img src={preview} alt="preview" className="w-full max-h-72 object-contain bg-muted" />
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-md p-6 space-y-4">
          <div className="overline">Resultado de extracción</div>
          {!result ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              <ImageIcon size={32} weight="duotone" className="mx-auto mb-3 opacity-40" />
              Sube un comprobante para ver el resultado.
            </div>
          ) : (
            <div className="space-y-3 animate-fade-in" data-testid="ocr-result">
              <Row label="Monto" value={fmt(result.monto)} mono />
              <Row label="Fecha" value={result.fecha || "—"} />
              <Row label="Nº Comprobante" value={result.numero_comprobante || "—"} mono />
              <Row label="Banco origen" value={result.banco_origen || "—"} />
              <Row
                label="Emisor (ordenante)"
                value={result.nombre_emisor || "—"}
                testid="row-emisor"
                hint="Confirme que coincide con el deudor; si difiere, podría ser un pago de un tercero."
              />

              {/* Validación de destinatario (Beneficiario) */}
              {result.beneficiario_extraido && (
                <div className="pt-2" data-testid="beneficiario-block">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Destinatario</span>
                    <span className="text-sm font-medium text-right max-w-[60%] truncate">
                      {result.beneficiario_extraido}
                    </span>
                  </div>
                  {result.beneficiario_status && (
                    <div
                      className={`flex items-center gap-2 p-2.5 rounded-md mt-2 text-xs ${
                        result.beneficiario_status === "VERIFICADO"
                          ? "bg-success/10 text-success"
                          : "bg-warning/10 text-warning"
                      }`}
                      data-testid={`beneficiario-${result.beneficiario_status === "VERIFICADO" ? "ok" : "alert"}`}
                    >
                      {result.beneficiario_status === "VERIFICADO" ? (
                        <CheckCircle size={16} weight="fill" />
                      ) : (
                        <XCircle size={16} weight="fill" />
                      )}
                      <div className="flex-1">
                        <div className="font-semibold">
                          {result.beneficiario_status === "VERIFICADO"
                            ? "Coincide con tu cuenta"
                            : result.beneficiario_status}
                        </div>
                        {result.beneficiario_match !== null && result.beneficiario_match !== undefined && (
                          <div className="opacity-75 mt-0.5">
                            Similitud: {Math.round(result.beneficiario_match * 100)}%
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {result.etiqueta && (() => {
                const { className, description, isSuccess } = getEtiquetaStyle(result.etiqueta);
                return (
                  <div className={`flex items-center gap-2 p-3 rounded-md mt-4 ${className}`}>
                    {isSuccess ? <CheckCircle size={18} weight="fill" /> : <XCircle size={18} weight="fill" />}
                    <div className="text-sm">
                      <div className="font-semibold">{result.etiqueta.replace("_", " ")}</div>
                      <div className="text-xs opacity-80 mt-0.5">{description}</div>
                    </div>
                  </div>
                );
              })()}

              {/* Recordatorio crítico: SIEMPRE verificar el pago en el banco */}
              <div
                className="flex items-start gap-3 p-4 rounded-md mt-4 border-l-4 border-warning bg-warning/10"
                data-testid="bank-verification-reminder"
                role="alert"
              >
                <Warning size={22} weight="duotone" className="text-warning shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-heading font-semibold text-foreground">
                    Importante: verifica el pago en tu cuenta bancaria
                  </div>
                  <div className="text-xs text-foreground/80 mt-1 leading-relaxed">
                    El agente OCR sólo lee la imagen del comprobante. <span className="font-semibold">No confirma que el dinero haya sido acreditado.</span>
                    {" "}Antes de dar por saldada la deuda o cerrar el caso, ingresa a tu banco y confirma que el abono/transferencia se refleje correctamente.
                    Muchos comprobantes pueden ser reversados o editados.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* History */}
      <div className="bg-card border border-border rounded-md">
        <div className="px-5 py-4 border-b border-border">
          <div className="overline">Historial</div>
          <h3 className="font-heading font-semibold mt-0.5">Comprobantes procesados</h3>
        </div>
        {receipts.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">No hay comprobantes aún.</div>
        ) : (
          <div className="divide-y divide-border">
            {receipts.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-5 py-3 text-sm gap-3" data-testid={`receipt-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{r.banco_origen || "Banco desconocido"} · Ref. {r.numero_comprobante || "—"}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {new Date(r.created_at).toLocaleString("es-EC")} · {r.fecha}
                    {r.nombre_emisor ? ` · ${r.nombre_emisor}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono font-medium">{fmt(r.monto)}</span>
                  {r.etiqueta && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getEtiquetaStyle(r.etiqueta).className}`}>
                      {r.etiqueta.replace("_", " ")}
                    </span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setImageError(false); setViewingReceipt(r); }}
                    data-testid={`view-receipt-${r.id}`}
                    title="Abrir comprobante"
                  >
                    <Eye size={14} weight="duotone" className="mr-1" /> Ver
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal para revisar el comprobante original */}
      <Dialog
        open={!!viewingReceipt}
        onOpenChange={(v) => {
          if (!v) {
            setViewingReceipt(null);
            setImageError(false);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="receipt-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Receipt size={20} weight="duotone" className="text-primary" />
              Comprobante {viewingReceipt?.numero_comprobante || ""}
            </DialogTitle>
          </DialogHeader>
          {viewingReceipt && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm bg-accent/30 rounded-md p-3">
                <div><span className="text-muted-foreground">Monto:</span> <span className="font-mono font-semibold">{fmt(viewingReceipt.monto)}</span></div>
                <div><span className="text-muted-foreground">Fecha:</span> <span className="font-mono">{viewingReceipt.fecha || "—"}</span></div>
                <div><span className="text-muted-foreground">Banco origen:</span> {viewingReceipt.banco_origen || "—"}</div>
                <div><span className="text-muted-foreground">Emisor:</span> {viewingReceipt.nombre_emisor || "—"}</div>
                <div className="col-span-2"><span className="text-muted-foreground">Beneficiario extraído:</span> {viewingReceipt.beneficiario_extraido || "—"}</div>
                {viewingReceipt.etiqueta && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Etiqueta:</span>
                    <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${getEtiquetaStyle(viewingReceipt.etiqueta).className}`}>
                      {viewingReceipt.etiqueta.replace("_", " ")}
                    </span>
                  </div>
                )}
              </div>

              <div className="rounded-md border border-border overflow-hidden bg-muted/20 grid place-items-center min-h-[300px]">
                {imageError ? (
                  <div className="p-6 text-center text-sm text-muted-foreground" data-testid="receipt-image-fallback">
                    Este comprobante fue procesado antes de guardar imagen original. Vuelve a subirlo si necesitas revisarlo.
                  </div>
                ) : (
                  <img
                    src={receiptApi.imageUrl(viewingReceipt.id)}
                    alt={`Comprobante ${viewingReceipt.numero_comprobante || viewingReceipt.id}`}
                    className="max-w-full max-h-[60vh] object-contain"
                    data-testid="receipt-viewer-image"
                    onError={() => setImageError(true)}
                  />
                )}
              </div>

              <div className="flex justify-end gap-2">
                <a
                  href={receiptApi.imageUrl(viewingReceipt.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="receipt-download-link"
                >
                  <Button variant="outline">Abrir en nueva pestaña</Button>
                </a>
                <Button
                  onClick={() => {
                    setViewingReceipt(null);
                    setImageError(false);
                  }}
                  data-testid="receipt-viewer-close"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, mono, testid, hint }) {
  return (
    <div data-testid={testid} className="py-2 border-b border-border last:border-0">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
        <span className={`text-sm font-medium text-right max-w-[60%] truncate ${mono ? "font-mono" : ""}`}>
          {value}
        </span>
      </div>
      {hint && (
        <div className="text-[11px] text-muted-foreground mt-1 italic leading-snug">{hint}</div>
      )}
    </div>
  );
}
